import { getAPIUrl } from '../../config/api.config';
import { getAuthTokenAsync, notifySessionExpired } from '../api/authTokenManager';
import toast from 'react-hot-toast';
import { logger, logPayment } from '../../utils/logger';

export interface CreateTransactionRequest {
  userId: string;
  planId: string;
  paymentMethod?: string;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  isRenewal?: boolean;
  promoCode?: string;
}

export interface TransactionResponse {
  payment_url: string;
  reference: string;
  order_id: string;
}

export interface TransactionStatusResponse {
  merchantOrderId: string;
  reference: string;
  amount: string;
  statusCode: string;
  statusMessage: string;
}

export class PaymentService {
  private static instance: PaymentService;
  private apiUrl = getAPIUrl();

  private constructor() {
    // Singleton pattern
  }

  static getInstance(): PaymentService {
    if (!PaymentService.instance) {
      PaymentService.instance = new PaymentService();
    }
    return PaymentService.instance;
  }

  async createTransaction(request: CreateTransactionRequest): Promise<TransactionResponse> {
    const url = `${this.apiUrl}/api/payments/create-transaction`;
    logPayment('Creating transaction', { url, planId: request.planId, paymentMethod: request.paymentMethod });

    const token = await getAuthTokenAsync();
    if (!token) {
      throw new Error('Authentication required. Please log in and try again.');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      notifySessionExpired();
      throw new Error('Sesi berakhir, silakan login kembali');
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({})) as { error?: string; message?: string };
      const errorMsg = errorBody.error || errorBody.message || `HTTP ${response.status}: ${response.statusText}`;
      logPayment('Transaction creation failed', { status: response.status, error: errorMsg });
      throw new Error(errorMsg);
    }

    const result = await response.json() as { success?: boolean; data?: TransactionResponse; error?: string; message?: string };
    logPayment('Transaction response received', { success: result.success, hasData: !!result.data });

    if (!result.success) {
      throw new Error(result.error || result.message || 'Transaction creation failed');
    }

    if (!result.data) {
      throw new Error('Invalid response: missing transaction data');
    }

    if (!result.data.payment_url) {
      logPayment('Response data missing payment_url', { data: result.data });
      throw new Error('Payment URL not received from server');
    }

    return result.data;
  }

  redirectToPayment(paymentUrl: string): void {
    logPayment('Redirecting to Duitku payment page', { paymentUrl });

    const parsed = new URL(paymentUrl);
    if (parsed.protocol !== 'https:' || !(parsed.hostname.endsWith('duitku.com') || parsed.hostname.endsWith('duitku.co.id'))) {
      throw new Error('Invalid payment URL: not a legitimate Duitku domain');
    }

    window.location.href = paymentUrl;
  }

  async processPayment(request: CreateTransactionRequest): Promise<void> {
    try {
      toast.loading('Memproses pembayaran...');

      // Create transaction via backend (validates response internally)
      const transaction = await this.createTransaction(request);

      toast.dismiss();

      logPayment('Redirecting to payment page', {
        orderId: transaction.order_id,
        reference: transaction.reference
      });

      // Show redirect message briefly
      toast('Mengalihkan ke halaman pembayaran...', { icon: '🔄', duration: 2000 });

      // Redirect to Duitku payment page
      this.redirectToPayment(transaction.payment_url);
    } catch (error) {
      toast.dismiss();
      logger.error('Payment process failed', { error });
      toast.error(`Gagal memproses pembayaran: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  async getTransactionStatus(orderId: string): Promise<TransactionStatusResponse> {
    const token = await getAuthTokenAsync();
    const response = await fetch(`${this.apiUrl}/api/payments/status/${orderId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      notifySessionExpired();
      throw new Error('Sesi berakhir, silakan login kembali');
    }

    if (!response.ok) {
      throw new Error('Failed to get transaction status');
    }

    const result = await response.json() as { data: TransactionStatusResponse };
    return result.data;
  }

  // Helper method to validate phone number (Indonesian format)
  validatePhoneNumber(phone: string): boolean {
    const phoneRegex = /^(\+62|62|0)[0-9]{9,13}$/;
    return phoneRegex.test(phone);
  }

  // Helper method to format phone number to +62 format
  formatPhoneNumber(phone: string): string {
    let cleaned = phone.replace(/\D/g, '');

    if (cleaned.startsWith('0')) {
      cleaned = `62${cleaned.substring(1)}`;
    }

    if (!cleaned.startsWith('62')) {
      cleaned = `62${cleaned}`;
    }

    return `+${cleaned}`;
  }
}

export default PaymentService.getInstance();
